import React from 'react';
import { Box, Card, CardHeader, CardContent, Collapse, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TablePagination, TableRow, Typography, } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import API, { Types } from '@wheeebo/polaris-api';
import { SquareChip } from '../chip';
import { CircleLoader, LoadError } from '../loader';
import { FlightPathMap, GoogleMapProvider } from '../map/google';
import { VehiclePanel } from '../panel';
const INITIAL_ITEM_COUNT = 5;
const msgs = defineMessages({
    headerTitle: {
        id: 'pages.member.member.OperationListCard.header.title',
        defaultMessage: 'Upcoming Operations in Charge',
    },
    headerSubTitle: {
        id: 'pages.member.member.OperationListCard.header.subTitle',
        defaultMessage: 'Flight plans assigned to this member',
    },
});
function DateRow({ label, startAt, endAt }) {
    const intl = useIntl();
    return (React.createElement(React.Fragment, null,
        React.createElement(Grid, { item: true, xs: 4, sx: { display: 'flex', alignItems: 'center' } }, label),
        React.createElement(Grid, { item: true, xs: 8 },
            React.createElement(Grid, { container: true },
                React.createElement(Grid, { item: true, xs: 3 }, "From:"),
                React.createElement(Grid, { item: true, xs: 9 }, startAt ? intl.formatTime(startAt, { year: 'numeric', month: 'numeric', day: '2-digit' }) : null),
                React.createElement(Grid, { item: true, xs: 3 }, "To:"),
                React.createElement(Grid, { item: true, xs: 9 }, endAt ? intl.formatTime(endAt, { year: 'numeric', month: 'numeric', day: '2-digit' }) : null)))));
}
function Row({ apiKey, flightPlan, onMissionSelected, onVehicleSelected, onFlightPathSelected }) {
    const intl = useIntl();
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    return (React.createElement(React.Fragment, null,
        React.createElement(TableRow, { hover: true, onClick: () => setOpen((e) => !e) },
            React.createElement(TableCell, { sx: {
                    width: '100%',
                    pl: 1,
                    borderLeftWidth: 3,
                    borderLeftStyle: 'solid',
                    borderLeftColor: flightPlan?.lastFlightLog?.status === Types.FlightLogStatusExecuting
                        ? theme.palette.error.light
                        : flightPlan?.lastFlightLog?.status === Types.FlightLogStatusPaused
                            ? theme.palette.info.light
                            : flightPlan?.lastFlightLog?.status === Types.FlightLogStatusCompleted
                                ? theme.palette.success.light
                                : flightPlan?.lastFlightLog?.status === Types.FlightLogStatusIncompleted
                                    ? theme.palette.warning.light
                                    : theme.palette.divider,
                } },
                React.createElement(Stack, { gap: 1 },
                    React.createElement(Stack, { direction: "row", gap: 1, sx: { display: 'flex', alignItems: 'center' } },
                        React.createElement(Box, null,
                            React.createElement(Typography, { variant: "body2", sx: { display: 'inline' } }, flightPlan.flightPath.name),
                            React.createElement(Typography, { variant: "body2", sx: { display: 'inline', opacity: 0.7 } }, ` at `),
                            React.createElement(Typography, { variant: "body2", sx: { display: 'inline', textDecoration: 'underline' } }, intl.formatTime(flightPlan.startAt, { year: 'numeric', month: 'numeric', day: '2-digit' }))),
                        flightPlan?.lastFlightLog?.status === Types.FlightLogStatusExecuting ? (React.createElement(SquareChip, { label: "Executing", color: "error" })) : flightPlan?.lastFlightLog?.status === Types.FlightLogStatusPaused ? (React.createElement(SquareChip, { label: "Paused", color: "info" })) : flightPlan?.lastFlightLog?.status === Types.FlightLogStatusCompleted ? (React.createElement(SquareChip, { label: "Completed", color: "success" })) : flightPlan?.lastFlightLog?.status === Types.FlightLogStatusIncompleted ? (React.createElement(SquareChip, { label: "Incompleted", color: "warning" })) : null),
                    React.createElement(Box, { sx: { width: '100%', minHeight: 180, position: 'relative', flex: 1 } },
                        React.createElement(GoogleMapProvider, null,
                            React.createElement(FlightPathMap, { apiKey: apiKey, flightPathId: flightPlan.flightPath.id, hideAllUIs: true }))))),
            React.createElement(TableCell, { align: "right" }, open ? React.createElement(KeyboardArrowUpIcon, { fontSize: "small" }) : React.createElement(KeyboardArrowDownIcon, { fontSize: "small" }))),
        React.createElement(TableRow, null,
            React.createElement(TableCell, { colSpan: 2, style: { paddingBottom: open ? undefined : 0, paddingTop: open ? undefined : 0 } },
                React.createElement(Collapse, { in: open },
                    React.createElement(Grid, { container: true, spacing: 1 },
                        React.createElement(Grid, { item: true, xs: 4, sx: { display: 'flex', alignItems: 'center' } }, "Mission"),
                        React.createElement(Grid, { item: true, xs: 8, sx: { cursor: 'pointer' }, onClick: () => flightPlan?.flightPath?.mission ? onMissionSelected(flightPlan.flightPath.mission.id) : undefined }, flightPlan.flightPath.mission.name),
                        React.createElement(Grid, { item: true, xs: 4, sx: { display: 'flex', alignItems: 'center' } }, "Flight path"),
                        React.createElement(Grid, { item: true, xs: 8, sx: { cursor: 'pointer' }, onClick: () => flightPlan?.flightPath?.mission
                                ? onFlightPathSelected(flightPlan.flightPath.mission.id, flightPlan.flightPath.id)
                                : undefined }, flightPlan.flightPath.name),
                        React.createElement(Grid, { item: true, xs: 4, sx: { display: 'flex', alignItems: 'center' } }, "Vehicle"),
                        React.createElement(Grid, { item: true, xs: 8 },
                            React.createElement(VehiclePanel, { vehicle: flightPlan.vehicle, onClick: () => onVehicleSelected(flightPlan.vehicle.id) })),
                        React.createElement(DateRow, { label: "Scheduled date", startAt: flightPlan.startAt, endAt: flightPlan.endAt }),
                        React.createElement(DateRow, { label: "Last execution", startAt: flightPlan.lastFlightLog?.startAt, endAt: flightPlan.lastFlightLog?.endAt })))))));
}
function OperationListCard({ apiKey, memberId, onMissionSelected, onVehicleSelected, onFlightPathSelected, ...props }) {
    const intl = useIntl();
    const [page, setPage] = React.useState(0);
    const [now, setNow] = React.useState();
    const options = {
        limit: INITIAL_ITEM_COUNT,
        offset: page * INITIAL_ITEM_COUNT,
        operatorId: memberId,
        startAt: now,
        preload: [
            'vehicle',
            'flight_path',
            'flight_path_mission',
            'last_flight_log',
            'last_flight_log_operator',
            'last_flight_log_vehicle',
        ],
    };
    const { data, isError, isLoading } = useQuery(API.FlightPlans.listKey(options), () => API.FlightPlans.list(options), {
        enabled: !!now,
    });
    React.useEffect(() => setNow(new Date()), []);
    return (React.createElement(Card, { ...props },
        React.createElement(CardHeader, { title: intl.formatMessage(msgs.headerTitle), subheader: intl.formatMessage(msgs.headerSubTitle) }),
        React.createElement(CardContent, null,
            isLoading ? React.createElement(CircleLoader, null) : null,
            isError ? React.createElement(LoadError, null) : null,
            data && data.total > 0 ? (React.createElement(TableContainer, { component: Paper, elevation: 0 },
                React.createElement(Table, null,
                    React.createElement(TableBody, null, data?.items?.map((e) => (React.createElement(Row, { key: e.id, apiKey: apiKey, flightPlan: e, onVehicleSelected: onVehicleSelected, onMissionSelected: onMissionSelected, onFlightPathSelected: onFlightPathSelected })))),
                    React.createElement(TableFooter, null,
                        React.createElement(TableRow, null,
                            React.createElement(TablePagination, { rowsPerPageOptions: [INITIAL_ITEM_COUNT], count: data?.total || 0, rowsPerPage: INITIAL_ITEM_COUNT, page: page, SelectProps: {
                                    inputProps: { 'aria-label': 'rows per page' },
                                }, onPageChange: (_, newPage) => setPage(newPage) })))))) : (React.createElement(Typography, { variant: "body2" }, "No record found.")))));
}
export default React.memo(OperationListCard);
